/**
 * `DataTable` — tabella generica, riusabile e paginata del design system.
 *
 * Nasce dalla generalizzazione della tabella di `SkillList` (`/skills`): estrae
 * ordinamento client‑side per colonna, stati (loading / error / vuoto) e
 * paginazione client‑side, così da poter essere riusata su qualsiasi elenco.
 *
 * Caratteristiche:
 *  - **Generica** sul tipo di riga `T` tramite `columns` dichiarative;
 *  - **Ordinabile**: le colonne con `sortable` mostrano intestazioni cliccabili
 *    (`aria-sort` corretto). Un clic ordina asc, un secondo inverte la direzione;
 *  - **Paginata** lato client con controlli precedente/successivo e scelta della
 *    dimensione pagina;
 *  - **Stati**: caricamento, errore e stato vuoto uniformi;
 *  - **Controllabile o non**: sort e pagina possono essere gestiti dal chiamante
 *    (controlled) oppure lasciati allo stato interno del componente.
 *
 * Presentazionale e senza dipendenze di dominio: l'ordinamento di default è una
 * permutazione stabile; per logiche di ordinamento particolari (es. `null` in
 * coda) passare un `sortComparator` per colonna.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { EmptyState } from './EmptyState';

/** Direzione dell'ordinamento. */
export type SortDirection = 'asc' | 'desc';

/** Criterio di ordinamento: chiave di colonna + direzione. */
export interface SortState {
  key: string;
  direction: SortDirection;
}

/** Definizione dichiarativa di una colonna. */
export interface Column<T> {
  /** Chiave stabile e univoca della colonna. */
  key: string;
  /** Intestazione della colonna. */
  header: ReactNode;
  /** Contenuto della cella per una riga. */
  render: (row: T) => ReactNode;
  /** Se la colonna è ordinabile (default: `false`). */
  sortable?: boolean;
  /**
   * Comparatore per l'ordinamento. Richiesto (o `sortAccessor`) se `sortable`.
   * Riceve le due righe e la direzione corrente e restituisce il numero **già
   * orientato** in stile `Array.prototype.sort` (il componente NON moltiplica
   * per la direzione). Ricevere la direzione permette regole speciali, come
   * mantenere certi valori (es. `null`) sempre in coda a prescindere dal verso.
   */
  sortComparator?: (a: T, b: T, direction: SortDirection) => number;
  /**
   * Alternativa semplice a `sortComparator`: estrae un valore ordinabile. In
   * questo caso la direzione è applicata automaticamente dal componente.
   */
  sortAccessor?: (row: T) => string | number;
  /** `colSpan` opzionale della cella (non applicato all'intestazione). */
  colSpan?: number;
  /**
   * `colSpan` **dipendente dalla riga**. Quando restituisce `n > 1`, la cella
   * occupa `n` colonne e le successive `n − 1` colonne NON emettono la propria
   * cella per quella riga (le rispettive `render` vengono saltate). Utile per
   * mostrare un controllo che si estende su più colonne solo per certe righe
   * (es. un editor inline). Prevale su `colSpan` quando entrambi sono definiti.
   */
  cellSpan?: (row: T) => number;
  /** Classe CSS opzionale applicata a `<th>` e `<td>` della colonna. */
  className?: string;
}

export interface DataTableProps<T> {
  /** Righe complete (non paginate): il componente pagina/ordina internamente. */
  rows: readonly T[];
  /** Definizione delle colonne. */
  columns: Column<T>[];
  /** Estrae una chiave React stabile per la riga. */
  rowKey: (row: T, index: number) => string | number;

  /** Etichetta accessibile della tabella. */
  caption?: string;
  /** Classe CSS aggiuntiva del contenitore. */
  className?: string;

  /* --- Stati ------------------------------------------------------- */
  loading?: boolean;
  error?: string | null;
  /** Contenuto dello stato vuoto (default: messaggio generico). */
  emptyMessage?: ReactNode;

  /* --- Ordinamento (opzionalmente controllato) --------------------- */
  sort?: SortState | null;
  defaultSort?: SortState | null;
  onSortChange?: (next: SortState) => void;

  /* --- Paginazione (opzionalmente controllata) --------------------- */
  /** Abilita la paginazione (default: `true`). */
  paginated?: boolean;
  /** Dimensione pagina iniziale/non controllata (default: `10`). */
  pageSize?: number;
  /** Opzioni selezionabili di dimensione pagina (default: `[10, 25, 50]`). */
  pageSizeOptions?: number[];
  /** Pagina corrente controllata (1‑based). */
  page?: number;
  onPageChange?: (page: number) => void;
}

/**
 * Costruisce il comparatore **già orientato** per la direzione data.
 *  - `sortComparator`: riceve la direzione e decide da sé come orientarsi.
 *  - `sortAccessor`: confronto ascendente semplice, orientato qui dal segno.
 */
function makeComparator<T>(
  col: Column<T>,
  direction: SortDirection,
): (a: T, b: T) => number {
  if (col.sortComparator) {
    const cmp = col.sortComparator;
    return (a, b) => cmp(a, b, direction);
  }
  if (col.sortAccessor) {
    const accessor = col.sortAccessor;
    const sign = direction === 'desc' ? -1 : 1;
    return (a, b) => {
      const va = accessor(a);
      const vb = accessor(b);
      if (va < vb) return -sign;
      if (va > vb) return sign;
      return 0;
    };
  }
  return () => 0;
}

/** Indicatore visuale della direzione di ordinamento corrente. */
function sortIndicator(active: boolean, direction: SortDirection): string {
  if (!active) return '';
  return direction === 'asc' ? ' ▲' : ' ▼';
}

/** Marcatore di ellissi nella barra dei numeri di pagina. */
const ELLIPSIS = 'ellipsis' as const;
type PageItem = number | typeof ELLIPSIS;

/**
 * Calcola gli elementi della paginazione numerica con ellissi.
 * Mostra sempre la prima e l'ultima pagina, più una finestra attorno a quella
 * corrente; le lacune sono compresse in `…` (es. `1 … 3 4 5 … 23`).
 *
 * @param current pagina corrente (1‑based)
 * @param totalPages numero totale di pagine (>= 1)
 * @param siblings quante pagine mostrare per lato attorno alla corrente
 */
function buildPageItems(
  current: number,
  totalPages: number,
  siblings = 1,
): PageItem[] {
  // Soglia oltre la quale conviene comprimere con le ellissi. Con poche pagine
  // le mostriamo tutte: 1 (first) + 1 (last) + current ± siblings + 2 ellissi.
  const maxVisible = siblings * 2 + 5;
  if (totalPages <= maxVisible) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const left = Math.max(current - siblings, 1);
  const right = Math.min(current + siblings, totalPages);

  // Mostriamo l'ellissi solo se c'è davvero almeno una pagina nascosta.
  const showLeftEllipsis = left > 2;
  const showRightEllipsis = right < totalPages - 1;

  const items: PageItem[] = [1];

  if (showLeftEllipsis) {
    items.push(ELLIPSIS);
  } else {
    // Nessuna ellissi a sinistra: riempi le pagine 2..left-1.
    for (let p = 2; p < left; p++) items.push(p);
  }

  for (let p = left; p <= right; p++) {
    if (p !== 1 && p !== totalPages) items.push(p);
  }

  if (showRightEllipsis) {
    items.push(ELLIPSIS);
  } else {
    for (let p = right + 1; p < totalPages; p++) items.push(p);
  }

  items.push(totalPages);
  return items;
}

export function DataTable<T>({
  rows,
  columns,
  rowKey,
  caption,
  className,
  loading = false,
  error = null,
  emptyMessage = 'Nessun dato da mostrare.',
  sort,
  defaultSort = null,
  onSortChange,
  paginated = true,
  pageSize = 10,
  pageSizeOptions = [10, 25, 50],
  page,
  onPageChange,
}: DataTableProps<T>) {
  // Ordinamento: controllato dal chiamante se `sort` è fornito, altrimenti interno.
  const [internalSort, setInternalSort] = useState<SortState | null>(defaultSort);
  const activeSort = sort !== undefined ? sort : internalSort;

  // Paginazione: pagina e dimensione (dimensione sempre interna, pagina opzionale).
  const [internalPage, setInternalPage] = useState(1);
  const [size, setSize] = useState(pageSize);
  const currentPage = page ?? internalPage;

  const columnByKey = useMemo(() => {
    const map = new Map<string, Column<T>>();
    for (const col of columns) map.set(col.key, col);
    return map;
  }, [columns]);

  const handleSort = (key: string) => {
    const col = columnByKey.get(key);
    if (!col?.sortable) return;
    const next: SortState =
      activeSort?.key === key
        ? { key, direction: activeSort.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' };
    if (onSortChange) onSortChange(next);
    if (sort === undefined) setInternalSort(next);
    // Ordinare riporta all'inizio: coerente con la maggior parte delle tabelle.
    goToPage(1);
  };

  const goToPage = (next: number) => {
    if (onPageChange) onPageChange(next);
    if (page === undefined) setInternalPage(next);
  };

  // Applica l'ordinamento in modo stabile senza mutare l'input.
  const sortedRows = useMemo(() => {
    if (!activeSort) return rows.slice();
    const col = columnByKey.get(activeSort.key);
    if (!col) return rows.slice();
    const comparator = makeComparator(col, activeSort.direction);
    return rows
      .map((item, index) => ({ item, index }))
      .sort((a, b) => {
        const cmp = comparator(a.item, b.item);
        return cmp !== 0 ? cmp : a.index - b.index;
      })
      .map((d) => d.item);
  }, [rows, activeSort, columnByKey]);

  const total = sortedRows.length;
  const totalPages = paginated ? Math.max(1, Math.ceil(total / size)) : 1;
  // Se la pagina corrente eccede il totale (es. dopo un filtro), clampa in view.
  const safePage = Math.min(currentPage, totalPages);
  const pageRows = paginated
    ? sortedRows.slice((safePage - 1) * size, safePage * size)
    : sortedRows;

  const containerClass = ['ui-table', className].filter(Boolean).join(' ');

  if (loading) {
    return (
      <div className={containerClass}>
        <p className="ui-table__status" role="status">
          Caricamento in corso…
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={containerClass}>
        <p className="ui-table__status ui-table__status--error" role="alert">
          {error}
        </p>
      </div>
    );
  }

  if (total === 0) {
    return (
      <div className={containerClass}>
        <EmptyState role="status">{emptyMessage}</EmptyState>
      </div>
    );
  }

  const rangeStart = paginated ? (safePage - 1) * size + 1 : 1;
  const rangeEnd = paginated ? rangeStart + pageRows.length - 1 : total;
  const pageItems = paginated ? buildPageItems(safePage, totalPages) : [];

  return (
    <div className={containerClass}>
      <table className="ui-table__table">
        {caption && <caption className="ui-table__caption">{caption}</caption>}
        <thead>
          <tr>
            {columns.map((col) => {
              const active = activeSort?.key === col.key;
              const ariaSort: 'ascending' | 'descending' | 'none' = active
                ? activeSort!.direction === 'asc'
                  ? 'ascending'
                  : 'descending'
                : 'none';
              return (
                <th
                  key={col.key}
                  scope="col"
                  className={col.className}
                  aria-sort={col.sortable ? ariaSort : undefined}
                >
                  {col.sortable ? (
                    <button
                      type="button"
                      className="ui-table__sort"
                      onClick={() => handleSort(col.key)}
                    >
                      {col.header}
                      {sortIndicator(active, activeSort?.direction ?? 'asc')}
                    </button>
                  ) : (
                    col.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {pageRows.map((row, index) => {
            // Numero di colonne successive da saltare per effetto di un
            // `cellSpan` dinamico della colonna corrente.
            let skip = 0;
            return (
              <tr key={rowKey(row, index)}>
                {columns.map((col) => {
                  if (skip > 0) {
                    skip -= 1;
                    return null;
                  }
                  const dynamicSpan = col.cellSpan?.(row);
                  const span = dynamicSpan ?? col.colSpan;
                  if (dynamicSpan && dynamicSpan > 1) {
                    skip = dynamicSpan - 1;
                  }
                  return (
                    <td key={col.key} className={col.className} colSpan={span}>
                      {col.render(row)}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>

        {paginated && (
          <tfoot className="ui-table__foot">
            <tr>
              <td colSpan={columns.length}>
                <div className="ui-table__pagination">
                  {/* Sinistra: riepilogo del range visibile. */}
                  <span className="ui-table__range" aria-live="polite">
                    {rangeStart}–{rangeEnd} di {total}
                  </span>

                  {/* Centro: numeri di pagina con ellissi. */}
                  <nav className="ui-table__pages" aria-label="Paginazione">
                    <button
                      type="button"
                      className="ui-btn ui-btn--sm ui-btn--icon ui-table__page"
                      onClick={() => goToPage(safePage - 1)}
                      disabled={safePage <= 1}
                      aria-label="Pagina precedente"
                    >
                      ‹
                    </button>

                    {pageItems.map((item, i) =>
                      item === ELLIPSIS ? (
                        <span
                          key={`ellipsis-${i}`}
                          className="ui-table__ellipsis"
                          aria-hidden="true"
                        >
                          …
                        </span>
                      ) : (
                        <button
                          key={item}
                          type="button"
                          className={[
                            'ui-btn',
                            'ui-btn--sm',
                            'ui-table__page',
                            item === safePage ? 'ui-table__page--active' : '',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                          aria-label={`Pagina ${item}`}
                          aria-current={item === safePage ? 'page' : undefined}
                          onClick={() => goToPage(item)}
                        >
                          {item}
                        </button>
                      ),
                    )}

                    <button
                      type="button"
                      className="ui-btn ui-btn--sm ui-btn--icon ui-table__page"
                      onClick={() => goToPage(safePage + 1)}
                      disabled={safePage >= totalPages}
                      aria-label="Pagina successiva"
                    >
                      ›
                    </button>
                  </nav>

                  {/* Destra: numero di record per pagina. */}
                  <div className="ui-table__page-size">
                    <label>
                      Righe per pagina{' '}
                      <span className="ui-select ui-select--sm">
                        <select
                          value={size}
                          onChange={(e) => {
                            setSize(Number(e.target.value));
                            goToPage(1);
                          }}
                          aria-label="Righe per pagina"
                        >
                          {pageSizeOptions.map((opt) => (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </select>
                      </span>
                    </label>
                  </div>
                </div>
              </td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

export default DataTable;
